import { request, RequestExtendedOptions, Variables } from "graphql-request";
import {
  QueryGetSingleArticleArgs,
  ArticleSearchInput,
  PageType,
  QueryGetSupportedSportsArgs,
  AuthorSearchInput,
  ContentAuthorAuthoredContentArgs,
  QueryGetHomepageArgs,
  QueryGetProjectionTableMetadataArgs,
  QueryGetSlatesArgs,
  ProjectionsInput,
  PositionAbbrev,
  ShortFormNewsTypeEnum,
  ShortFormSearchInput,
  PromotionsInput,
  GetSingleAuthorQueryQueryVariables,
  NflScheduleSeasonTypeEnum,
  SportEnum,
  InputMaybe,
  TimestampBoundaries,
} from "@/graphql/gql/graphql";
import getActiveFixturesNflQuery from "@/graphql/queries/getActiveFixturesNflQuery";
import getActiveFixturesQuery from "@/graphql/queries/getActiveFixturesQuery";
import getAllTentpoleEvents from "@/graphql/queries/getAllTentpoleEventsQuery";
import getArticleBySlugQuery from "@/graphql/queries/getArticleBySlugQuery";
import getArticleInfoByDateRangeQuery from "@/graphql/queries/getArticleInfoByDateRangeQuery";
import getArticlesQuery from "@/graphql/queries/getArticlesQuery";
import getAuthorsQuery from "@/graphql/queries/getAuthorsQuery";
import getDivisionStandingsQuery from "@/graphql/queries/getDivisionStandingsQuery";
import getEfficiencyQuery from "@/graphql/queries/getEfficiencyQuery";
import getEventSummariesQuery from "@/graphql/queries/getEventSummariesQuery";
import getFeaturedArticles from "@/graphql/queries/getFeaturedArticles";
import getGameInjuriesQuery from "@/graphql/queries/getGameInjuriesQuery";
import getGameLeadersQuery from "@/graphql/queries/getGameLeadersQuery";
import getGameLogQuery from "@/graphql/queries/getGameLogQuery";
import getGameScoresQuery from "@/graphql/queries/getGameScoresQuery";
import getGolfEventsQuery from "@/graphql/queries/getGolfEventsQuery";
import { getHeadToHeadTopLevelDataQuery } from "@/graphql/queries/getHead2HeadTopLevelDataQuery";
import getHeadtoHeadBetSuccessQuery from "@/graphql/queries/getHeadToHeadBetSuccessQuery";
import getHeadToHeadFantasyProjectionsQuery from "@/graphql/queries/getHeadToHeadFantasyProjectionsQuery";
import getHeadToHeadHeaderQuery from "@/graphql/queries/getHeadToHeadHeaderQuery";
import getHeadToHeadOddsQuery from "@/graphql/queries/getHeadToHeadOddsQuery";
import getHeadToHeadPlayerGameLogQuery from "@/graphql/queries/getHeadToHeadPlayerGameLogQuery";
import getHeadToHeadPlayerGameStatsQuery from "@/graphql/queries/getHeadToHeadPlayerGameStatsQuery";
import getHeadToHeadPlayerPropsQuery from "@/graphql/queries/getHeadToHeadPlayerPropsQuery";
import getHeadToHeadPlayerStatsQuery from "@/graphql/queries/getHeadToHeadPlayerStatsQuery";
import getHeadToHeadPopularBetsQuery from "@/graphql/queries/getHeadToHeadPopularBetsQuery";
import getHeadToHeadTeamStatsQuery from "@/graphql/queries/getHeadToHeadTeamStatsQuery";
import getHomepageQuery from "@/graphql/queries/getHomepageQuery";
import getHorseRacesByTrackQuery from "@/graphql/queries/getHorseRacesByTrackQuery";
import getHorseRacesQuery from "@/graphql/queries/getHorseRacesQuery";
import getHorseTrackListQuery from "@/graphql/queries/getHorseTrackListQuery";
import getLiveCoreOddsQuery from "@/graphql/queries/getLiveCoreOdds";
import getMenusQuery from "@/graphql/queries/getMenus";
import getNflScheduleGamesQuery from "@/graphql/queries/getNflScheduleGamesQuery";
import getPlayerNewsHomeQuery from "@/graphql/queries/getPlayerNewsHome";
import getSportsPN from "@/graphql/queries/getPlayerNewsSports";
import getProjectionMetaDataQuery from "@/graphql/queries/getProjectionMetaDataQuery";
import getProjectionsQuery from "@/graphql/queries/getProjectionsQuery";
import { getPromotionsQuery } from "@/graphql/queries/getPromotionsQuery";
import getRaceByTrackQuery from "@/graphql/queries/getRaceByTrackQuery";
import getRedirectQuery from "@/graphql/queries/getRedirectQuery";
import getRunnerStatsQuery from "@/graphql/queries/getRunnerStatsQuery";
import getShortFormQuery from "@/graphql/queries/getShortFormQuery";
import getShortFormsQuery from "@/graphql/queries/getShortFormsQuery";
import getSingleAuthorQuery from "@/graphql/queries/getSingleAuthorQuery";
import getSitemapShortFormsQuery from "@/graphql/queries/getSitemapShortFormsQuery";
import getSiteSettingsQuery from "@/graphql/queries/getSiteSettingsQuery";
import getSlatesQuery from "@/graphql/queries/getSlatesQuery";
import getSubCategoriesQuery from "@/graphql/queries/getSubCategoriesQuery";
import getSubCategoryQuery from "@/graphql/queries/getSubCategoryQuery";
import getSingleSportBetsQuery from "@/graphql/queries/getSupportedSports/getSingleSportBets";
import getSports from "@/graphql/queries/getSupportedSports/getSports";
import getSupportedSportsFilters from "@/graphql/queries/getSupportedSports/getSupportedSportsFilters";
import getTentpoleEventQuery from "@/graphql/queries/getTentpoleEventQuery";
import getTrackDetailsQuery from "@/graphql/queries/getTrackDetailsQuery";
import getTrackRacesQuery from "@/graphql/queries/getTrackRacesQuery";
import getContentFilters from "../graphql/queries/getContentFilters/getContentFilters";
import isClient from "./isClient";
import { RetryOptions, withRetry } from "./retry";

export const DEFAULT_TIMEOUT = 25000;

export function WrapperSignalTimeout() {
  return AbortSignal.timeout(DEFAULT_TIMEOUT) as never;
}

// Same-origin proxy route (see pages/api/graphql.ts). The browser only ever
// talks to this path; the real orchestration endpoint stays server-side.
export const GRAPHQL_PROXY_PATH = "/research/api/graphql";

/**
 * Resolves the real upstream GraphQL endpoint. Server-only — the value is read
 * from process.env.ORCHESTRATION_ENDPOINT so it is never serialized into the
 * client bundle. Used by server-side data fetching and by the proxy route.
 */
export const getServerOrchestrationEndpoint = () => {
  return process.env?.ORCHESTRATION_ENDPOINT || "";
};
export function getRequestHeaders(requestId?: string) {
  return requestId ? { "x-request-id": requestId } : undefined;
}

/**
 * Head2Head queries are expensive upstream; retries cause a failing
 * matchup page query to multiply that load through repeated backoff attempts.
 */
const HEAD_TO_HEAD_RETRY_OPTIONS: RetryOptions = { retries: 0 };

/**
 * Wraps graphql-request's `request` with retry/backoff. Every graphql call in
 * this module goes through here so transient failures are retried with exponential backoff capped at 5s).
 * Generics mirror `request` so the resolved type is still inferred from the (typed) document.
 */
function retryRequest<T, V extends Variables = Variables>(
  options: RequestExtendedOptions<V, T>,
  retryOptions?: RetryOptions
): Promise<T> {
  return withRetry(() => request<T, V>(options), retryOptions);
}

export const getOrchestrationEndpoint = () => {
  // On the client, route through the same-origin proxy so the real
  // orchestration endpoint is never exposed to the browser. Resolve to an
  // absolute URL (graphql-request/fetch requires one).
  if (isClient()) {
    return `${globalThis.window.location.origin}${GRAPHQL_PROXY_PATH}`;
  }
  return getServerOrchestrationEndpoint() || "";
};

export function getMenus(isPreview?: boolean, retryOptions?: RetryOptions) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      variables: { isPreview },
      document: getMenusQuery,
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

export function getArticleBySlug(
  { slug, contentStatus, isPreview }: QueryGetSingleArticleArgs,
  requestId?: string
) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    requestHeaders: getRequestHeaders(requestId),
    document: getArticleBySlugQuery,
    variables: {
      slug,
      contentStatus,
      isPreview,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAllArticles(
  {
    limit = 10,
    afterCursor = null,
    sportName = null,
    hideDeprioritizedContent = null,
    ...rest
  }: ArticleSearchInput,
  requestId?: string,
  retryOptions?: RetryOptions
) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      requestHeaders: getRequestHeaders(requestId),
      document: getArticlesQuery,
      variables: {
        filter: {
          limit,
          sportName,
          afterCursor,
          hideDeprioritizedContent,
          ...rest,
        },
      },
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

export function getArticleInfoByDateRange({
  publishedWithin,
  limit = 0,
}: ArticleSearchInput) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getArticleInfoByDateRangeQuery,
    variables: {
      filter: {
        publishedWithin,
        limit,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getArticlesBySport(sports: string[]) {
  return Promise.all(
    sports.map((sportName) =>
      retryRequest({
        url: getOrchestrationEndpoint(),
        document: getArticlesQuery,
        variables: {
          filter: {
            sportName,
            afterCursor: "",
          },
        },
        signal: WrapperSignalTimeout(),
      })
    )
  );
}

export function getFeaturedArticlesBySport(
  sportSlugs: string[],
  requestId?: string,
  retryOptions?: RetryOptions
) {
  return Promise.all(
    sportSlugs.map((sportSlug) =>
      retryRequest(
        {
          url: getOrchestrationEndpoint(),
          requestHeaders: getRequestHeaders(requestId),
          document: getFeaturedArticles,
          variables: {
            filter: {
              sportSlug,
              pageType: sportSlug ? PageType.Sport : PageType.Homepage,
            },
          },
          signal: WrapperSignalTimeout(),
        },
        retryOptions
      )
    )
  );
}

export function getSubCategory(sport: string, subCategory: string) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getSubCategoryQuery,
    variables: {
      input: {
        sportName: sport,
        slug: subCategory,
      },
      filter: {
        limit: 16,
        subCategorySlug: subCategory,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getSubCategories() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getSubCategoriesQuery,
    signal: WrapperSignalTimeout(),
  });
}

interface GetShortFormsParams {
  filter: GetShortFormsParamsFilter;
  limit?: number;
}

interface GetShortFormsParamsFilter {
  start?: string;
  end?: string;
  sportName?: string;
  cursor?: string | null;
  teamNumberFireId?: number | null;
  positionAbbrev?: PositionAbbrev | null;
  shortFormNewsType?: ShortFormNewsTypeEnum | null;
  publishedWithin?: InputMaybe<TimestampBoundaries>;
}

export async function getShortForms(
  { filter, limit }: GetShortFormsParams,
  retryOptions?: RetryOptions
) {
  try {
    const {
      sportName,
      cursor,
      positionAbbrev,
      shortFormNewsType,
      teamNumberFireId,
      publishedWithin,
    } = filter;

    const queryParams = {
      filter: {
        limit,
        sport: sportName,
        afterCursor: cursor,
        team: {
          numberFireId: teamNumberFireId,
        },
        player: {
          positionAbbrev,
        },
        publishedWithin,
        shortFormNewsType,
      },
    };

    const response = await retryRequest(
      {
        url: getOrchestrationEndpoint(),
        document: getShortFormsQuery,
        variables: queryParams,
        signal: WrapperSignalTimeout(),
      },
      retryOptions
    );

    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in getShortForms API request:", {
      error,
      url: getOrchestrationEndpoint(),
    });
    throw error;
  }
}

export async function getSitemapShortForms({
  filter,
  limit,
}: GetShortFormsParams) {
  try {
    const {
      sportName,
      cursor,
      positionAbbrev,
      shortFormNewsType,
      teamNumberFireId,
      start,
      end,
    } = filter;

    const publishedWithin =
      start && end
        ? {
            start,
            end,
          }
        : undefined;

    const queryFilter: ShortFormSearchInput = {
      limit,
      sport: sportName,
      afterCursor: cursor,
      team: {
        numberFireId: teamNumberFireId,
      },
      publishedWithin,
      player: {
        positionAbbrev,
      },
      shortFormNewsType,
    };
    const queryParams = {
      filter: queryFilter,
    };

    const response = await retryRequest({
      url: getOrchestrationEndpoint(),
      document: getSitemapShortFormsQuery,
      variables: queryParams,
      signal: WrapperSignalTimeout(),
    });

    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in getSitemapShortForms API request:", error);
    throw error;
  }
}

export function getAuthors({
  contentType,
  sportName,
  sport,
  afterCursor,
  limit = 12,
}: AuthorSearchInput & ContentAuthorAuthoredContentArgs) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getAuthorsQuery,
    variables: {
      input: { sportName, afterCursor, limit },
      contentType,
      sport,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getSingleAuthor({ slug }: GetSingleAuthorQueryQueryVariables) {
  return withRetry(() =>
    request(getOrchestrationEndpoint(), getSingleAuthorQuery, {
      slug,
    })
  );
}

export function getScores(sport?: string) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getActiveFixturesQuery,
    variables: {
      input: { sport },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getScoresNfl() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getActiveFixturesNflQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getPlayerNewsHome({ sport, limit = 9 }: ShortFormSearchInput) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getPlayerNewsHomeQuery,
    variables: {
      filter: {
        sport,
        limit,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAvailableSports(
  { isPreview, featured }: QueryGetSupportedSportsArgs = {},
  requestId?: string
) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    requestHeaders: getRequestHeaders(requestId),
    document: getSports,
    variables: {
      isPreview,
      featured,
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getSingleSportBets(
  { slug }: { slug: string },
  requestId?: string
) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    requestHeaders: getRequestHeaders(requestId),
    document: getSingleSportBetsQuery,
    variables: {
      slug,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAvailableSportsPN(
  { isPreview, featured }: QueryGetSupportedSportsArgs = {},
  retryOptions?: RetryOptions
) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getSportsPN,
      variables: {
        isPreview,
        featured,
      },
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

export function getRedirect(slug: string, requestId?: string) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    requestHeaders: getRequestHeaders(requestId),
    document: getRedirectQuery,
    variables: {
      input: { slug },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHomepage({ isPreview }: QueryGetHomepageArgs) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getHomepageQuery,
    variables: { isPreview },
    signal: WrapperSignalTimeout(),
  });
}

export function getTentpoleEvent(slug = "", sportSlug = "") {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getTentpoleEventQuery,
    variables: {
      input: { slug, sportSlug },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getTentpoleEvents() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getAllTentpoleEvents,
    signal: WrapperSignalTimeout(),
  });
}

export function getProjectionInfo(
  args: QueryGetProjectionTableMetadataArgs,
  retryOptions?: RetryOptions
) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getProjectionMetaDataQuery,
      variables: args,
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

export function getSlates(variables: QueryGetSlatesArgs) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getSlatesQuery,
    variables,
    signal: WrapperSignalTimeout(),
  });
}

export function getGolfEvents() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getGolfEventsQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getProjectionData(args: ProjectionsInput) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getProjectionsQuery,
    variables: {
      input: args,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getShortForm(slug: string) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getShortFormQuery,
    variables: { slug },
    signal: WrapperSignalTimeout(),
  });
}

export function getSupportedSportsFiltersApi() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getSupportedSportsFilters,
    signal: WrapperSignalTimeout(),
  });
}

export function getContentFiltersApi() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getContentFilters,
    signal: WrapperSignalTimeout(),
  });
}

export function getUpcomingHorseRaces(
  { current = 0, results = 10 },
  retryOptions?: RetryOptions
) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHorseRacesQuery,
      variables: {
        profile: "FDR-Generic",
        current,
        results,
      },
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

interface GetRaceByTrackInput {
  trackCode: string;
  raceNumber: number;
}

export function getRaceByTrack({ trackCode, raceNumber }: GetRaceByTrackInput) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getRaceByTrackQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
      raceNumber,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getTrackRaces({ trackCode }: { trackCode: string }) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getTrackRacesQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHorseTrackList(retryOptions?: RetryOptions) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHorseTrackListQuery,
      variables: {
        profile: "FDR-Generic",
      },
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

export function getRunnerStats({ runnerName }: { runnerName: string }) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getRunnerStatsQuery,
    variables: {
      profile: "FDR-Generic",
      runnerName,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAllRacesByTrack({
  trackCode,
}: Partial<GetRaceByTrackInput>) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getHorseRacesByTrackQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getTrackDetails({ trackCode }: { trackCode: string }) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getTrackDetailsQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getSiteSettings() {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getSiteSettingsQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getPromotions({ tags }: PromotionsInput, requestId?: string) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    requestHeaders: getRequestHeaders(requestId),
    document: getPromotionsQuery,
    variables: {
      input: { tags },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadTopLevelData(numberFireGameId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadTopLevelDataQuery,
      variables: {
        input: { eventId: numberFireGameId },
      },
      signal: WrapperSignalTimeout(),
    },
    // Most important query, worth retrying at least once
    { ...HEAD_TO_HEAD_RETRY_OPTIONS, retries: 1 }
  );
}

export function getHeadToHeadPlayerStats(numberFireGameId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadPlayerStatsQuery,
      variables: {
        input: { eventId: numberFireGameId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getHeadToHeadTeamStats(numberFireGameId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadTeamStatsQuery,
      variables: {
        input: { eventId: numberFireGameId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getHeadToHeadBetSuccess(eventId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadtoHeadBetSuccessQuery,
      variables: {
        input: { eventId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getHeadToHeadFantasyProjections(numberFireGameId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadFantasyProjectionsQuery,
      variables: {
        input: { eventId: numberFireGameId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getHeadToHeadPopularBets(eventId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadPopularBetsQuery,
      signal: WrapperSignalTimeout(),
      variables: { input: { eventId } },
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getHeadToHeadPlayerGameLog(eventId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadPlayerGameLogQuery,
      variables: {
        input: { eventId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getHeadToHeadPlayerGameStats(numberFirePlayerId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadPlayerGameStatsQuery,
      variables: {
        input: {
          sport: SportEnum.Nfl,
          numberFireId: numberFirePlayerId,
        },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}
export function getHeadToHeadPlayerProps(eventId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadPlayerPropsQuery,
      variables: {
        input: { eventId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}
export function getHeadToHeadHeader(eventId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadHeaderQuery,
      variables: {
        input: { eventId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}
export function getHeadToHeadOdds(eventId: number) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getHeadToHeadOddsQuery,
      variables: {
        input: { eventId },
      },
      signal: WrapperSignalTimeout(),
    },
    HEAD_TO_HEAD_RETRY_OPTIONS
  );
}

export function getGameInjuries(eventId: number) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getGameInjuriesQuery,
    signal: WrapperSignalTimeout(),
    variables: { input: { eventId } },
  });
}

export function getGameLog(eventId: number) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getGameLogQuery,
    signal: WrapperSignalTimeout(),
    variables: { input: { eventId } },
  });
}

export function getDivisionStandings(retryOptions?: RetryOptions) {
  return retryRequest(
    {
      url: getOrchestrationEndpoint(),
      document: getDivisionStandingsQuery,
      signal: WrapperSignalTimeout(),
    },
    retryOptions
  );
}

export function getNflScheduleGames({ week }: { week?: number }) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getNflScheduleGamesQuery,
    variables: {
      input: week
        ? {
            week: week < 19 ? week : week - 18,
            seasonType:
              week < 19
                ? NflScheduleSeasonTypeEnum.Regular
                : NflScheduleSeasonTypeEnum.Post,
          }
        : undefined,
    },
    signal: AbortSignal.timeout(20000) as never,
  });
}

export function getTeamEfficiencyOverview(eventId: number) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getEfficiencyQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getGameLeaderStats(eventId: number) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getGameLeadersQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getEventSummaries(seasons: number[]) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getEventSummariesQuery,
    variables: { seasons },
    signal: WrapperSignalTimeout(),
  });
}

export function getGameScores(eventId: number) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getGameScoresQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getLiveCoreOdds(eventId: number) {
  return retryRequest({
    url: getOrchestrationEndpoint(),
    document: getLiveCoreOddsQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}
